// ---------------------------------------------------------------------------
// ชำระเงินออนไลน์ — ลิงก์ชำระของใบแจ้งหนี้ + หน้าสาธารณะ /pay/:code
// ---------------------------------------------------------------------------
// ฝั่งหน้าสาธารณะเรียก GS action `pay.public` (คืนเฉพาะฟิลด์ปลอดภัย) · โหมด localStorage ค้นจากข้อมูลในเครื่อง
// สถานะลิงก์: active | paid | expired | cancelled — active ที่เลยกำหนดนับเป็น expired ตอนอ่าน
// ---------------------------------------------------------------------------
import { gsEnabled, gsGet } from "./gsClient";
import { table } from "../mock/store";
import { paymentLinkRepo, settingRepo } from "../mock/repositories";
import { paymentService } from "./index";
import { settleLinkPayment } from "./settlement";
import { SETTINGS_KEY, CODE_PATTERN, DEFAULT_SETTINGS, addDaysIso, effectiveLinkStatus, newLinkCode, normalizeSettings } from "../config/online-payment";

const PUBLIC_SETTINGS = (settings) => {
  const s = normalizeSettings(settings);
  return { enabled: s.enabled, thankYou: s.thankYou };
};

export const onlinePaymentService = {
  getSettings: async (orgId) => normalizeSettings(await settingRepo.getOrgSetting(orgId, SETTINGS_KEY, DEFAULT_SETTINGS)),

  saveSettings: async (orgId, value) => {
    const clean = normalizeSettings(value);
    await settingRepo.saveOrgSetting(orgId, SETTINGS_KEY, clean);
    return clean;
  },

  /** สร้างลิงก์ชำระของใบแจ้งหนี้ — ถ้ามีลิงก์ที่ยังใช้ได้อยู่แล้วคืนอันเดิม (ไม่สร้างซ้ำ) */
  linkForInvoice: async ({ orgId, invoice }) => {
    if (!invoice || invoice.status === "paid") throw new Error("ใบแจ้งหนี้นี้ชำระแล้ว ไม่ต้องส่งลิงก์ชำระ");
    if (!(Number(invoice.total) > 0)) throw new Error("ยอดใบแจ้งหนี้ไม่ถูกต้อง");
    const existing = (await paymentLinkRepo.all(orgId, (row) => row.invoiceId === invoice.id)).find((row) => effectiveLinkStatus(row) === "active");
    if (existing) return existing;
    const settings = await onlinePaymentService.getSettings(orgId);
    return paymentLinkRepo.create(orgId, {
      code: newLinkCode(),
      title: `ชำระ ${invoice.docNo}`,
      invoiceId: invoice.id,
      docNo: invoice.docNo,
      customerId: invoice.customerId,
      customerName: invoice.customerName,
      amount: invoice.total,
      status: "active",
      expireAt: addDaysIso(settings.linkExpireDays),
    });
  },

  cancelLink: (orgId, id) => paymentLinkRepo.update(orgId, id, { status: "cancelled", cancelledAt: new Date().toISOString() }),

  /** ข้อมูลที่หน้าสาธารณะเห็น: null = ไม่พบ */
  lookup: async (code) => {
    const clean = String(code || "").trim();
    if (!CODE_PATTERN.test(clean)) return null;

    if (gsEnabled()) return gsGet("pay.public", { code: clean });

    const orgs = table("global", "organizations").all();
    for (const org of orgs) {
      const link = table(org.id, "paymentLinks").list((row) => String(row.code).toLowerCase() === clean.toLowerCase())[0];
      if (!link) continue;
      const invoice = link.invoiceId ? table(org.id, "invoices").find(link.invoiceId) : null;
      const settingRow = table(org.id, "orgSettings").list((row) => row.key === SETTINGS_KEY)[0];
      const status = invoice?.status === "paid" && link.status === "active" ? "paid" : effectiveLinkStatus(link);
      return {
        code: link.code,
        linkId: link.id,
        orgId: org.id,
        invoiceId: link.invoiceId || "",
        docNo: link.docNo || invoice?.docNo || "",
        title: link.title || "",
        amount: Number(link.amount) || 0,
        status,
        expireAt: link.expireAt || null,
        paidAt: link.paidAt || null,
        shop: {
          id: org.id, name: org.name, nameEn: org.nameEn, plan: org.plan, planExpireAt: org.planExpireAt,
          addons: org.addons, branding: org.branding, enabledModules: org.enabledModules, logoColor: org.logoColor,
        },
        settings: PUBLIC_SETTINGS(settingRow?.value),
      };
    }
    return null;
  },

  /** ลูกค้าเปิดสร้าง QR แล้ว -> จำ transaction ไว้ให้ร้านกดตรวจสอบสถานะได้ถ้าลูกค้าจ่ายแต่หน้าเว็บปิดไปก่อน */
  markPending: (orgId, linkId, qr) =>
    paymentLinkRepo.update(orgId, linkId, {
      pendingTransactionId: qr.transactionId,
      pendingRefNo: qr.refNo || "",
      pendingAt: new Date().toISOString(),
    }),

  /** ร้านกด "ตรวจสอบสถานะ" — inquiry ธุรกรรมที่ค้าง ถ้าสำเร็จ settle ให้ทันที */
  checkPending: async ({ orgId, link, organization }) => {
    if (!link?.pendingTransactionId) throw new Error("ลิงก์นี้ยังไม่มีธุรกรรมที่รอตรวจสอบ");
    const tid = link.pendingTransactionId;
    await paymentService.inquiry(tid).catch(() => {});
    const txn = await paymentService.getTransaction(tid);
    if (!txn) return { status: "unknown" };
    if (["success", "settled", "completed"].includes(txn.status)) {
      const result = await settleLinkPayment({ orgId, link, transactionId: tid, amount: txn.amount, refNo: txn.refNo, organization });
      return { status: "paid", ...result };
    }
    if (["failed", "expired", "cancelled", "reject", "rejected"].includes(txn.status)) {
      await paymentLinkRepo.update(orgId, link.id, { pendingTransactionId: "" });
      return { status: "failed" };
    }
    return { status: "pending" };
  },
};
